import { ColDef } from 'ag-grid-community';
import React, { useCallback, useRef, useState } from 'react';
import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { PreInvoice } from '../../models/PreInvoices';
import { SelectionToolbar } from '../interaction';

interface PreInvoicesAgGridTableProps {
  preInvoices: PreInvoice[];
  isLoading?: boolean;
  isFetching?: boolean;
  className?: string;
  selectedPreInvoices?: Set<number>;
  onClearSelection?: () => void;
  selectedColumns?: string[];
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onSelectionChanged?: (selectedPreInvoices: Set<number>) => void;
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
  onViewPreInvoice?: (preInvoiceId: number) => void;
  onInvoicePreInvoice?: (preInvoiceId: number) => void;
  onDeletePreInvoice?: (preInvoiceId: number) => void;
  onBulkInvoicePreInvoices?: () => void;
  onBulkMergePreInvoices?: () => void;
}

export const PreInvoicesAgGridTable = React.memo(function PreInvoicesAgGridTable({
  preInvoices,
  isFetching = false,
  selectedPreInvoices,
  onClearSelection,
  selectedColumns = [],
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onSelectionChanged,
  sorting,
  onSortingChange,
  onViewPreInvoice,
  onInvoicePreInvoice,
  onDeletePreInvoice,
  onBulkInvoicePreInvoices,
  onBulkMergePreInvoices,
}: PreInvoicesAgGridTableProps) {
  const agGridRef = useRef<AgGridTableRef>(null);
  const [localSelectedCount, setLocalSelectedCount] = useState(0);
  const selectionEnabled = true;

  const clearSelectionUI = useCallback(() => {
    agGridRef.current?.deselectAll();
    setLocalSelectedCount(0);
  }, []);

  const handleCloseSelectionToolbar = useCallback(() => {
    clearSelectionUI();
    onClearSelection?.();
  }, [clearSelectionUI, onClearSelection]);

  const { filteredColumnDefs } = useAgGridColumns({
    selectedColumns,
    onViewPreInvoice,
    onInvoicePreInvoice,
    onDeletePreInvoice,
  });

  const handleSelectionChanged = useCallback(
    (selectedRows: PreInvoice[]) => {
      if (!selectionEnabled) {
        return;
      }

      const selectedIds = new Set(selectedRows.map((preInvoice) => preInvoice.id));
      setLocalSelectedCount(selectedRows.length);

      if (onSelectionChanged) {
        onSelectionChanged(selectedIds);
      }
    },
    [onSelectionChanged, selectionEnabled],
  );

  React.useEffect(() => {
    if (!selectionEnabled) {
      clearSelectionUI();
      onClearSelection?.();
      return;
    }

    if (selectedPreInvoices && selectedPreInvoices.size === 0) {
      clearSelectionUI();
    }
  }, [selectedPreInvoices, clearSelectionUI, selectionEnabled, onClearSelection]);

  return (
    <div className={`w-full ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}>
      <AgGridTable
        ref={agGridRef}
        data={preInvoices}
        columnDefs={filteredColumnDefs as ColDef[]}
        height="calc(100vh - 150px)"
        enableSelection={selectionEnabled}
        onSelectionChanged={handleSelectionChanged}
        onGridReady={() => undefined}
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        gridOptions={{}}
        sorting={sorting}
        onSortingChange={onSortingChange}
        rowHeight={50}
      />

      {selectionEnabled && (
        <SelectionToolbar
          selectedCount={localSelectedCount || selectedPreInvoices?.size || 0}
          onClose={handleCloseSelectionToolbar}
          onFacturar={onBulkInvoicePreInvoices}
          onMerge={onBulkMergePreInvoices}
        />
      )}
    </div>
  );
});
